import type { ResourceType } from '@cotera/api';
import type { OpenArtifactRequest } from './artifact-panel-context';
import { artifactTypeAsResourceType } from './artifacts';

/** A push into the artifact panel that should be recorded on the conversation. */
export type ArtifactRegistration = {
  resourceType: ResourceType;
  resourceId: string;
};

/**
 * How a push into the artifact panel should be registered on the conversation
 * (an `add_artifact` chat action), or `null` when it stays panel-local.
 *
 * Registering is what makes an artifact part of the *chat* rather than of this
 * mount: it survives reload, and the conversation reports it in the LLM's active
 * context, so the model knows what the user is looking at.
 *
 * Only two kinds of push are excluded:
 * - **Non-resource artifacts.** A workflow node's config or a chart rendered
 *   from tool output has no workspace resource to point the chat at.
 * - **Background pushes** (`focus: false`). Those are derived tabs — an agent's
 *   `@`-mentioned documents — that the opener re-derives on mount and prunes
 *   when the mention goes away. Persisting them would resurrect tabs the user
 *   never opened.
 */
export const artifactRegistrationFor = (
  request: OpenArtifactRequest
): ArtifactRegistration | null => {
  if (request.focus === false) {
    return null;
  }
  const resourceId = request.artifactId.trim();
  if (resourceId === '') {
    return null;
  }
  const resourceType = artifactTypeAsResourceType(request.type);
  if (resourceType === null) {
    return null;
  }
  return { resourceType, resourceId };
};
